// English — LowFodmaps's own strings, the reference for pt.ts. The platform's
// strings (sign-in, pricing, paywall, billing, team, language) live in
// @quoter/web-core and are merged underneath these in ./index.ts; a section
// named the same there (auth, nav, settings) is merged key by key, so a line
// declared here overrides the platform's. Placeholders use {name}, filled with
// fmt() from useI18n().
//
// Tone: plain and kind. The reader may be in discomfort and is not a
// nutritionist. No promises of a cure; always "usually", never "safe".

export const t = {
  auth: {
    emailPlaceholder: 'you@example.com',
    heroTitle: 'A calmer gut, one food at a time.',
    heroSubline:
      'Look up any food, follow a step-by-step reintroduction plan, and keep a simple diary of what you ate and how you felt.',
  },
  // Overrides the platform's team-oriented wording: a LowFodmaps account is one person.
  language: {
    hint: 'Menus, dates and food names.',
  },
  nav: {
    today: 'Today',
    foods: 'Foods',
    plan: 'Plan',
  },
  levels: {
    low: 'Low',
    moderate: 'Moderate',
    high: 'High',
  },
  groups: {
    fructans: 'Fructans',
    gos: 'GOS',
    lactose: 'Lactose',
    fructose: 'Fructose',
    sorbitol: 'Sorbitol',
    mannitol: 'Mannitol',
  },
  scores: {
    label: 'How does your gut feel?',
    s0: 'Fine',
    s1: 'Mild',
    s2: 'Noticeable',
    s3: 'Bad',
    s4: 'Severe',
  },
  disclaimer:
    'A general guide, not medical advice. Portions matter and research keeps moving. If you can, do this diet with a dietitian — and see a doctor before starting if your symptoms are new.',
  foods: {
    title: 'Food table',
    subtitle: 'Ratings are for a typical single serving. {count} foods and counting.',
    search: 'Search a food…',
    allLevels: 'All',
    allCategories: 'All foods',
    anyGroup: 'Any FODMAP',
    showing: '{n} foods',
    none: 'Nothing matches. Try a shorter word, or clear the filters.',
    clear: 'Clear filters',
    colFood: 'Food',
    colLevel: 'FODMAP',
    colWhy: 'What to watch',
    nothingToWatch: 'Nothing in particular',
    legendTitle: 'Reading the table',
    legendLow: 'Usually fine at a normal portion.',
    legendModerate: 'A small portion is usually fine; a full one may not be.',
    legendHigh: 'Leave out during elimination. Test it during reintroduction.',
    categories: {
      vegetables: 'Vegetables',
      fruit: 'Fruit',
      grains: 'Bread and grains',
      protein: 'Meat, fish, eggs, tofu',
      dairy: 'Dairy and alternatives',
      legumes: 'Beans and lentils',
      nuts: 'Nuts and seeds',
      sweets: 'Sugar and sweets',
      drinks: 'Drinks',
      pantry: 'Oils, sauces, seasonings',
    },
    notes: {
      smallOk: 'A small portion is usually fine.',
      largeAdds: 'Fine at a normal portion; large ones add up.',
      ripeness: 'Firm and just-ripe is low. Very ripe, spotty ones are high.',
      greenOnly: 'Green part only. The white bulb is high.',
      cannedRinsed: 'Canned and well rinsed, in a small portion.',
      checkLabel: 'Check the label for onion, garlic, honey, inulin or wheat.',
      firmOnly: 'Firm and pressed. Silken tofu is high.',
      infused: 'FODMAPs do not dissolve in oil, so the flavour comes without them.',
      alcohol: 'Low in FODMAPs, but alcohol itself can irritate the gut.',
      soyProtein: 'Read the ingredients: "soy protein" is low, "whole soybeans" is high.',
      weakBrew: 'Weak and short-brewed. Strong black tea is higher.',
    },
  },
  publicTable: {
    kicker: 'Free low-FODMAP food table',
    title: 'Can I eat this?',
    lede: 'Search {count} everyday foods. Green is usually fine, amber in small portions, red is one to leave out for now.',
    ctaTitle: 'Know what to avoid. Now find out what you can bring back.',
    ctaBody:
      'LowFodmaps walks you through reintroduction one FODMAP at a time and keeps a diary of what you ate and how you felt, so you end up with your own list — not a generic one.',
    cta: 'Try LowFodmaps free for {n} days',
    signIn: 'Sign in',
  },
  today: {
    title: 'Today',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    backToToday: 'Back to today',
    whatDidYouEat: 'What did you eat?',
    placeholder: 'Rice, grilled chicken, carrots…',
    add: 'Add',
    emptyDay: 'Nothing logged for this day yet.',
    remove: 'Remove',
    noteLabel: 'Anything worth remembering?',
    notePlaceholder: 'Bloated after lunch, slept badly, stressful day…',
    saveNote: 'Save note',
    saved: 'Saved',
    weekTitle: 'Your last 7 days',
    weekEmpty: 'Check in each day and a pattern starts to show here.',
    notLogged: 'Not logged',
    meals: {
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      dinner: 'Dinner',
      snack: 'Snack',
      drink: 'Drink',
    },
    phaseCard: {
      elimination: 'Elimination · day {n}',
      eliminationNoDate: 'Elimination phase',
      reintroduction: 'Reintroduction · {done} of {total} tested',
      personalization: 'Your own diet',
      openPlan: 'Open the plan',
      activeChallenge: 'Testing now: {name}',
    },
  },
  plan: {
    title: 'Your plan',
    subtitle: 'Three phases. Most people take eight to twelve weeks in all.',
    youAreHere: 'You are here',
    moveHere: 'I am in this phase',
    phases: {
      elimination: {
        name: '1. Elimination',
        length: '2 to 6 weeks',
        body: 'Swap high-FODMAP foods for low ones and let things settle. It is a short test, not a way of eating forever: if you feel no better after six weeks, FODMAPs are probably not your trigger — stop and talk to a professional.',
      },
      reintroduction: {
        name: '2. Reintroduction',
        length: '6 to 8 weeks',
        body: 'Stay on the elimination diet and test one FODMAP group at a time: the same food on three days, a little more each day. Then rest for three days and move to the next.',
      },
      personalization: {
        name: '3. Your own diet',
        length: 'From then on',
        body: 'Bring back everything you tolerated, keep small portions of what you half-tolerated, and re-test the rest every few months. Tolerance changes.',
      },
    },
    startedOn: 'Elimination started on',
    dayN: 'Day {n} of elimination',
    readyHint: 'Symptoms settled for a week or more? You can start reintroducing.',
    challengesTitle: 'Reintroduction challenges',
    challengesHint:
      'One at a time, in any order — many people start with the foods they miss most. Keep everything else low-FODMAP while you test.',
    progress: '{done} of {total} tested',
    start: 'Start this challenge',
    testFood: 'Test food',
    day: 'Day {n}',
    portion: 'Portion',
    howWasIt: 'How was it?',
    dayNotePlaceholder: 'Optional note',
    notDoneYet: 'Not done yet',
    restFirst: 'Finished? Rest for {n} days on the elimination diet before the next challenge.',
    stopEarly: '“Noticeable” or worse? Stop there — no need to go up to the next portion.',
    verdictTitle: 'Your verdict',
    suggested: 'suggested',
    reset: 'Start over',
    resetConfirm: 'Clear this challenge and its notes?',
    oneAtATime: 'Finish “{name}” before starting another.',
    status: {
      not_started: 'Not tested',
      in_progress: 'Testing',
      tolerated: 'Tolerated',
      limited: 'Small portions',
      avoid: 'Avoid for now',
    },
    verdictHelp: {
      tolerated: 'No real trouble. Bring it back.',
      limited: 'Fine until the bigger portion. Keep it small.',
      avoid: 'Trouble from the start. Leave it out and re-test in a few months.',
    },
    challenges: {
      lactose: {
        name: 'Lactose',
        found: 'Milk, yogurt, ice cream, soft cheese',
        food: "Cow's milk",
        day1: '½ glass (125 ml)',
        day2: '1 glass (250 ml)',
        day3: '1½ glasses (375 ml)',
      },
      fructose: {
        name: 'Fructose',
        found: 'Honey, mango, asparagus, agave',
        food: 'Honey',
        day1: '1 teaspoon',
        day2: '2 teaspoons',
        day3: '1 tablespoon',
      },
      sorbitol: {
        name: 'Sorbitol',
        found: 'Avocado, blackberries, stone fruit, sugar-free gum',
        food: 'Avocado',
        day1: '¼ avocado',
        day2: '½ avocado',
        day3: '1 avocado',
      },
      mannitol: {
        name: 'Mannitol',
        found: 'Mushrooms, cauliflower, celery, sweet potato',
        food: 'Button mushrooms',
        day1: '2 mushrooms',
        day2: '4 mushrooms',
        day3: '8 mushrooms',
      },
      fructans_wheat: {
        name: 'Fructans — wheat',
        found: 'Bread, pasta, couscous, biscuits',
        food: 'Wheat bread',
        day1: '1 slice',
        day2: '2 slices',
        day3: '3 slices',
      },
      fructans_onion: {
        name: 'Fructans — onion',
        found: 'Onion, leek, shallot, most ready-made sauces',
        food: 'Cooked onion',
        day1: '1 tablespoon',
        day2: '2 tablespoons',
        day3: '¼ onion',
      },
      fructans_garlic: {
        name: 'Fructans — garlic',
        found: 'Garlic, garlic powder, most ready-made sauces',
        food: 'Cooked garlic',
        day1: '¼ clove',
        day2: '½ clove',
        day3: '1 clove',
      },
      gos: {
        name: 'GOS',
        found: 'Beans, lentils, chickpeas, cashews, pistachios',
        food: 'Canned chickpeas, rinsed',
        day1: '2 tablespoons',
        day2: '4 tablespoons',
        day3: '½ cup',
      },
    },
  },
  consent: {
    title: 'Before you start logging',
    intro:
      'LowFodmaps works by storing what you write in it: what you eat, how your gut feels, and how your food tests go. That is information about your health, so we ask first.',
    data: 'I agree to LowFodmaps storing the food, symptom and test entries I choose to log, so it can show them back to me. Nobody else sees them, they are never used for advertising, and I can download or delete them at any time.',
    notMedical:
      'I understand LowFodmaps is a tracking tool, not a doctor or a dietitian. It does not give medical advice, and I am 18 or older.',
    readMore: 'The details:',
    agree: 'Agree and continue',
    withdraw: 'You can change your mind later: Settings → Delete what I logged.',
  },
  settings: {
    deleteTitle: 'Delete what I logged',
    deleteHint: 'Erases your diary, check-ins, food tests and plan. It also withdraws your consent to store them. This cannot be undone.',
    delete: 'Delete all my entries',
    deleting: 'Deleting…',
    deleted: 'Everything you logged has been deleted.',
    deleteConfirm: 'Delete everything you have logged in LowFodmaps? This cannot be undone.',
    deleteAccount: 'To close your account as well, cancel under Billing and write to hello@mudacorp.com — we remove it within 30 days.',
    dataTitle: 'Your data',
    dataHint: 'Everything you logged, in one file. It is yours.',
    export: 'Download my data',
    exporting: 'Preparing…',
    exportError: 'Could not prepare the file. Please try again.',
  },
} as const

// `as const` gives literal value types, which a translation could never
// satisfy. Widen every leaf to string so pt.ts can be typed `ProductStrings`.
// (Declared here rather than imported from @quoter/web-core so this file stays
// free of the shell's React code.)
type Widen<T> = T extends string ? string : { readonly [K in keyof T]: Widen<T[K]> }

export type ProductStrings = Widen<typeof t>
